<template>
  <div class="w-full">
    <div v-if="!errorPage">

    <div style="height: 100%" v-if="loading">
      <loader size="xxs" :loader-image="false" />
    </div>

    <div class="mt-6 w-full px-3" v-else>
      <div class="flex w-full flex-col">
        <div class="flex flex-col w-full">
          <div class="flex flex-col w-full">
            <h1 class="font-[800] font-extrabold mt-3 uppercase">
              Performance Agreements
            </h1>

            <div class="warning mt-2 border border-[#E993235C] bg-[#E9932314]">
              <Icon icon-name="info" size="l" class=" mr-2" style="fill: transparent;"/>
              <div style="font-size: 12px"> <p class="mb-4">
                Simple targets are a straightforward approach to managing objectives focused on one specific outcome.
                 They are best applied to objectives that are focused and quantifiable on a single metric. For example, Increase sales by 10% this quarter.
              </p>
             <p>
              Criteria-based targets are based on a set of criteria or standards, often multi-dimensional and consider different possible outcomes.
               They are best applied to complex objectives to account for multiple factors, conditions, or potential outcomes. It offers fuller pictures of success.
             </p></div>
            </div>
            <div class="flex w-full items-center my-2">
              <radio-button
                :options="document_options"
                :col-span="`col-span-4`"
                class="text-darkPurple"
                row-gap="gap-y-2 gap-x-10"
                space-between="mr-3"
                v-model="settingsPayload.editDocument"
                @change="agreementEdit($event)"
              />
            </div>

            <hr class=""/>

            <h1 class="font-[800] font-extrabold mt-5 uppercase flex">
              Performance Ratings
              <div
                style="position: relative; display: inline-block"
                @mouseover="showRating = true"
                @mouseleave="showRating = false"
              >
                <Icon
                  class="ml-3 text-flame"
                  icon-name="icon-info"
                  size="xsm"
                  style="cursor: pointer"
                />
                <ToolTip v-if="showRating" width="298px" left="-127px">
                  <template v-slot:header>
                    <p class="font-12 uppercase font-semibold">
                      Performance Rating
                    </p>
                  </template>
                  <p style="font-size: 14px">
                    Here you can decide your organization's rating scale for
                    appraisals. This sets a limit for appraisers, and they
                    cannot give an appraisal score higher or lower than the
                    specified range.
                  </p>
                </ToolTip>
              </div>
            </h1>
            <div class="flex w-full ">
              <p class="text-[14px] flex flex-grow ">
                The system is preconfigured to use the rating below.  Edit your ratings if it is different from the default rating below.
              </p>
              <span @click="handleRatingsClick(Ratings.length === 0 ? `Setup` : `Edit`)"  class="cursor-pointer text-flame " style="font-size: 11px;">
                <icon icon-name="icon-edit" size="xs" class="inline-block mr-1" />{{ Ratings.length === 0 ? `SETUP` : `EDIT RATINGS` }}
              </span>
            </div>
          </div>
          <hr class="my-3"/>

          <div
            v-if="RatingsData.length > 0"
            class="w-full flex flex-row justify-start gap-24 bg-ghostWhite p-3 my-3"
            style="border-radius: 5px"
          >
            <div class="flex flex-col w-max-content justify-start">
              <div
                class="w-max-content my-3 text-base"
                v-for="(ratings, index) in RatingsData.slice(0, 3)"
                :key="index"
              >
                {{ ratings.rating }}<span v-if="RatingsData[0].type !== 'absolute'">%</span> = {{ ratings.description }}
              </div>
            </div>

            <div class="flex flex-col justify-start">
              <div
                class="w-full my-3 text-base"
                v-for="(ratings, index) in RatingsData.slice(3, 6)"
                :key="index"
              >
                {{ ratings.rating }}<span v-if="RatingsData[0].type !== 'absolute'">%</span> = {{ ratings.description }}
              </div>
            </div>
          </div>
        </div>

       <hr class="mt-5" />
      </div>

      <div class="flex flex-wrap">
        <div class="flex w-full flex-col">
          <h1 class="font-[800] font-extrabold mt-5 uppercase flex">
            Performance Bands            <div
              style="position: relative; display: inline-block"
              @mouseover="showRanking = true"
              @mouseleave="showRanking = false"
            >
              <Icon
                class="ml-3 text-flame"
                icon-name="icon-info "
                size="xsm"
                style="cursor: pointer"
              />
              <ToolTip v-if="showRanking" width="298px" left="-127px">
                <template v-slot:header>
                  <p class="font-12 uppercase font-semibold">
                    Performance Ranking
                  </p>
                </template>
                <p style="font-size: 14px">
                  In performance ranking, you are classifying the various rates
                  within bands and defining what your organization describes
                  such performance level.
                </p>
              </ToolTip>
            </div>
          </h1>
          <div class="flex w-full ">
            <p class="w-4/5 flex flex-grow text-[14px]">
              Click to manage your organization's performance rankings and flags.
            </p>

            <span @click="handleEditRanking(RatingsData.length === 0 ? `Setup` : `Edit`)"  class="cursor-pointer text-flame" style="font-size: 11px;">
              <icon icon-name="icon-edit" size="xs" class="inline-block " /> {{ Ranking.length === 0 ? `SETUP` : `MANAGE RANKINGS` }}
          </span>

          </div>

          <hr class="mt-3 "/>

          <div class="flex w-full flex-col pr-4">
            <Table
              :headers="AppraisalHeader"
              :items="Ranking"
              class="w-full mt-1"
              :has-checkbox="false"
              :has-number="false"
              :loading="false"
              v-if="Ranking.length > 0"
            >
              <template v-slot:item="{ item }">
                <div v-if="item.id">
                  <p class="py-3" v-if="item.index === 0">
                    From
                    <span class="font-bold">
                      {{ item.data.from }}<span v-if="mode !== 'absolute'">%</span>
                    </span>
                    to
                    <span class="font-bold">
                      {{ item.data.to }}<span v-if="mode !== 'absolute'">%</span>
                    </span>
                  </p>
                  <p class="py-3" v-else>
                    Above
                    <span class="font-bold">
                      {{ item.data.from }}<span v-if="mode !== 'absolute'">%</span>
                    </span>
                    to
                    <span class="font-bold">
                      {{ item.data.to }}<span v-if="mode !== 'absolute'">%</span>
                    </span>
                  </p>
                </div>
                <div v-else-if="item.description">
                  <p class="py-3">
                    {{ item.data.description.description }}
                  </p>
                </div>
                <div v-else-if="item.flag">
                  <p class="py-3">
                    {{ item.data.flag }}
                  </p>
                </div>
              </template>
            </Table>
          </div>
        </div>
        <div>
          <hr class="my-6"/>

        <h1 class="font-[800] font-extrabold mt-5 uppercase flex">
          Mid-Term Appraisal
        </h1>
        <p>
          Configure the method for conducting mid-term performance appraisals by selecting either the standard rating scale or appraisal flag indicators, based on your organizational preference.
        </p>

        <div class="flex w-full items-center">
          <radio-button
            :options="appraisal_options"
            :col-span="`col-span-4`"
            class="text-darkPurple"
            row-gap="gap-y-2 gap-x-10"
            space-between="mr-3"
            v-model="settingsPayload.appraisalMethod"
            @change="handleAppraisalMethodChange"
          />
        </div>
      </div>
      </div>
    </div>

    <Modal v-if="showAppraisalConfirmationModal">
      <Card class="p-5 flex flex-col max-w-md">
        <div class>
          <p class="font-bold ml-4 my-5 text-center">
            Are you sure you want to change the appraisal method to <strong>{{ selectedAppraisalMethodName }}</strong>?
          </p>
          <p class="text-sm text-gray-600 mb-5 text-center">
            This change will affect how performance appraisals are conducted in your organization.
          </p>
          <div class="flex">
            <Button class="bg-dynamicBackBtn text-white" @click="confirmAppraisalMethodChange">
              Yes
            </Button>
            <Button
              background-color="none"
              class="text-dynamicBackBtn border border-solid border-dynamicBackBtn ml-4"
              @click="showAppraisalConfirmationModal = false"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Card>
    </Modal>

    <transition name="slider">
      <SideModal v-if="showDescription" top="15%" left="76%" width="328px">
        <template v-slot:title>
          <div class="text-darkPurple -mt-2 w-11/12">Rating Descriptions</div>
          <div @click="showDescription = false">
            <Icon
              icon-name="close"
              size="s"
              style="cursor: pointer; margin: 0px 0px 0px 10px"
            />
          </div>
        </template>
        <div class="flex flex-col mt-6">
          <div
            class="w-full h-5 text-base text-jet mt-2 mb-2"
            v-for="(ratings, index) in Ratings"
            :key="index"
          >
            {{ ratings.rating }} = {{ ratings.description }}
          </div>
        </div>
      </SideModal>
    </transition>

      <ValidationObserver v-slot="{ handleSubmit }">
        <RightSideBar
          v-if="showModal"
          submit="Save"
          @submit="handleSubmit(onSubmit)"
          @close="closeSideBar()"
          button-class="bg-dynamicBackBtn text-white"
          width="width:605px !important"
        >
          <template v-slot:title>
            <p class="text-darkPurple">Update Performance Ratings</p>
          </template>
          <template v-slot:subtitle>
            <p class="font-semibold text-darkPurple mb-3">
              Set the lowest and highest ratings as seen during appraisal ratings.
            </p>
          </template>
          <div class="overflow-y-auto" style="height:75vh;scrollbar-width:none;">

          <div class="flex items-center space-x-1 mb-4 w-full">
            <radio-button
              :options="ratingOptions"
              v-model="mode"
              @change="updateRatings($event)"
              :col-span="` w-[max-content] mr-4`"
              class="text-darkPurple w-full "
              style="margin-top: 0px !important; display: flex !important;"
              row-gap="!flex gap-2 "
              space-between="mr-3"
            />
          </div>
            <div class="w-full flex mb-6 pb-4">
            <div class="w-1/2 pr-4">
              <div class="w-full">
                <label class="block text-sm font-medium text-gray-700 mb-2">Lowest Rating</label>
                <div class="w-full px-3 py-2 border border-gray-300 rounded-lg">
                  {{ mode === 'absolute' ? lowestValue : ('0 %') }}
                </div>
              </div>
            </div>
            <div class="w-1/2 pl-4">
              <div class="w-full">
                <label class="block text-sm font-medium text-gray-700 mb-2">Highest Rating</label>
                <div class="w-full px-3 py-2 border border-gray-300 rounded-lg">
                  {{ mode === 'absolute' ? highestValue : ('100 %') }}
                </div>
              </div>
            </div>
          </div>

          <div class="mb-2">
            <p class="font-semibold text-darkPurple text-base">Rating Descriptions</p>
          </div>
            <div v-if="mode === 'fixed'" class="warning mt-2 border border-[#E993235C] my-5 bg-[#E9932314]">
              <Icon icon-name="info" size="m" class=" mr-2" style="fill: transparent;"/>
              <div style="font-size: 12px" class="my-auto"> <p class="">
                Users can rate with any of the fixed values below.</p>
           </div>
          </div>

            <div v-if="mode === 'floating'" class="warning mt-2 border border-[#E993235C] my-5 bg-[#E9932314]">
              <Icon icon-name="info" size="m" class=" mr-2" style="fill: transparent;"/>
              <div style="font-size: 12px" class="my-auto"> <p class="">
                Users can rate with any value between {{ 0 }} % and {{ 100 }} %.
              </p>
           </div>
          </div>

          <div v-if="mode === 'absolute'" class="w-full">
            <div class="p-2 bg-ghostWhite w-full flex rounded-sm">
              <div class="w-2/12 uppercase font-black text-xs text-romanSilver">Rating</div>
              <div class="w-10/12 -ml-3 uppercase font-black text-xs text-romanSilver">Description</div>
            </div>

            <div
              class=" flex items-end" style="width:98%;"
              v-for="item in Ratings"
              :key="item.rating"
            >
              <div style="width: 10%">
                <c-text
                  class="w-full -mt-2"
                  disabled
                  v-model="item.rating"
                />
              </div>
              <div class="w-10/12 ml-4">
                <c-text
                  class="w-full"
                  v-model="item.description"
                  placeholder="Rating Description"
                />
              </div>
            </div>
          </div>
          <div v-if="mode === 'fixed' || mode === 'floating'" class="w-full">
            <div class="p-2 bg-ghostWhite w-full flex rounded-sm">
              <div class="w-2/12 uppercase font-black text-xs text-romanSilver">Rating</div>
              <div class="w-10/12 -ml-3 uppercase font-black text-xs text-romanSilver">Description</div>
            </div>

            <div
              class="w-full flex items-end my-3" style="width:98%;"
              v-for="item in Ratings"
              :key="item.rating"
            >
              <div style="width: 18%">
                <c-text
                  class="w-10 -mt-2 p-0 flex items-center justidy-center"
                  style="width:65px;display:flex;align-items:center;padding:0px;"
                  disabled
                  :value="`${item.rating}${mode !== 'absolute' ? '%' : ''}`"
                />
              </div>
              <div class="w-10/12 ml-6">
                <c-text
                  class="w-full"
                  v-model="item.description"
                  placeholder="Rating Description"
                />
              </div>
            </div>
          </div>
          </div>

        </RightSideBar>
      </ValidationObserver>
    </div>

    <div v-else>
      <ErrorPage />
    </div>
  </div>
</template>

<script>
import { ValidationObserver } from "vee-validate";
import CText from "@scelloo/cloudenly-ui/src/components/text";
import Table from "@scelloo/cloudenly-ui/src/components/table";
import ToolTip from "@/components/ToolTip";
import RightSideBar from "@/components/RightSideBar";
import SideModal from "@/components/SideModal";
import Icon from "@/components/Icon";
import ErrorPage from "@/modules/Admin/error403";
import RadioButton from "@/components/RadioButton";
import Modal from "@/components/Modal";
import Card from "@/components/Card";
import Button from "@/components/Button";

export default {
  components: {
    ValidationObserver,
    Table,
    ToolTip,
    ErrorPage,
    SideModal,
    Icon,
    RightSideBar,
    CText,
    RadioButton,
    Modal,
    Card,
    Button
  },
  name: "AppraisalScores",
  data() {
    return {
      mode: '',
      lowestValue: "",
      highestValue: "",
      lowest_value: null,
      highest_value: null,
      showMore: false,
      showDescription: false,
      Ranking: [],
      Ratings: [],
      RatingsData: [],
      showRating: false,
      showRanking: false,
      showModal: false,
      appraisalModal: false,
      errorPage: false,
      loading: false,
      isSettingModeFromData: false,
      AppraisalHeader: [
        { title: "PERFORMANCE BAND", value: "id" },
        { title: "PERFORMANCE DESCRIPTION", value: "description" },
        { title: "APPRAISAL FLAG", value: "flag" },
      ],
      ratingPayload: [],
      ratingDescriptions: [],
      descriptions: [],
      appraisalName: "",
      appraisal_options: [
        {
          name: "Use regular ratings",
          radioName: "appraisal_method",
          value: "regular_ratings",
        },
        {
          name: "Use appraisal flags",
          radioName: "appraisal_method",
          value: "appraisal_flags",
        }
      ],
      document_options: [
        {
          name: "Use Simple Targets",
          radioName: "document",
          value: "manager",
        },
        {
          name: "Use Criteria-Based Targets",
          radioName: "document",
          value: "employee",
        },
        { name: "Use Both", radioName: "document", value: "both" },
      ],
      settingsPayload: {
        appraisalMethod: "regular_ratings",
        editDocument: "manager",
        settingId: null,
      },
      ratingOptions: [
        { name: 'Use Absolute Values', radioName: 'rating', value: 'absolute' },
        { name: 'Use Fixed %', radioName: 'rating', value: 'fixed' },
        { name: 'Use Floating %', radioName: 'rating', value: 'floating' },
      ],
      showAppraisalConfirmationModal: false,
      selectedAppraisalMethodName: '',
    };
  },

  watch: {
    lowestValue(value) {
      const ratings = [];
      const descriptions = this.descriptions.toString().split(",");

      if (value === "") {
        this.ratingPayload = [];
      } else if (value >= 0) {
        for (let i = value; i <= this.highestValue; i++) {
          ratings.push({
            orgId: this.$orgId,
            rating: i,
            description: descriptions[i - value],
          });
        }
        this.ratingPayload = ratings;
      }
    },

    highestValue(value) {
      const ratings = [];
      const descriptions = this.descriptions.toString().split(",");

      if (value === "") {
        this.ratingPayload = [];
      } else if (value >= 0) {
        for (let i = this.lowestValue; i <= Number(value); i++) {
          ratings.push({
            orgId: this.$orgId,
            rating: i,
            description: descriptions[i - this.lowestValue],
          });
        }
        this.ratingPayload = ratings;
      }
    },

  },

  methods: {
    updateRatings(newMode) {
      try {
        if (newMode === 'absolute') {
          this.Ratings = [
            { rating: 0, description: 'Very Poor' },
            { rating: 1, description: 'Poor' },
            { rating: 2, description: 'Below Average' },
            { rating: 3, description: 'Average' },
            { rating: 4, description: 'Good' },
            { rating: 5 , description: 'Excellent' }
          ];
        }
        else if (newMode === 'fixed') {
          this.Ratings = [
            { rating: 0, description: 'Very Poor' },
            { rating: 20, description: 'Poor' },
            { rating: 40, description: 'Below Average' },
            { rating: 60, description: 'Average' },
            { rating: 80, description: 'Good' },
            { rating: 100, description: 'Excellent' }
          ];
        }
        else if (newMode === 'floating') {
          this.Ratings = [];
        }
      } catch (e) {
        this.Ratings = [];
      }
    }
  ,
    async handleEditRanking(action) {
      switch (action) {
        case "Setup":
          try {
            await this.$handlePrivilege(
              "performance",
              "manageRankings"
            );
            this.$router.push({ name: "EditRankings" });
          } catch (error) {
            this.$toasted.error(
              "You do not have permission to perform this task",
              {
                duration: 5000,
              }
            );
          }
          break;

        case "Edit":
          try {
            await this.$handlePrivilege(
              "performance",
              "manageRankings"
            );
            this.$router.push({ name: "EditRankings" });
          } catch (error) {
            this.$toasted.error(
              "You do not have permission to perform this task",
              {
                duration: 5000,
              }
            );
          }
          break;

        default:
          return true;
      }
      return "";
    },

    async handleRatingsClick(action) {
      switch (action) {
        case "Setup":
          try {
            await this.$handlePrivilege(
              "performance",
              "editRatings"
            );
            this.showModal = true;
          } catch (error) {
            this.$toasted.error(
              "You do not have permission to perform this task",
              {
                duration: 5000,
              }
            );
          }
          break;

        case "Edit":
          try {
            await this.$handlePrivilege(
              "performance",
              "editRatings"
            );
            this.showModal = true;
          } catch (error) {
            this.$toasted.error(
              "You do not have permission to perform this task",
              {
                duration: 5000,
              }
            );
          }
          break;

        default:
          return true;
      }
      return "";
    },

      onSubmit() {
        this.loading = true;
        let response;
        if (this.mode === 'absolute') {
          this.$_getPerfomanceRatingByType('absolute')
            .then((res) => {
              response = res;
              this.Ratings = res?.data?.data || [];
              this.RatingsData = res?.data?.data || [];
              this.normalizeRatings();
              return this.$_getRanking();
            })
            .then(() => this.getPerfomanceRating())
            .then(() => {
              this.$toasted.success(response.data.message, { duration: 5000 });
              this.closeSideBar();
              this.loading = false;
            })
            .catch(() => {
              this.Ratings = [];
              this.$toasted.error("Error in creating Performance rating", {
                duration: 5000,
              });
              this.loading = false;
            });
        }
        else if (this.mode === 'fixed') {
          this.$_getPerfomanceRatingByType('fixed')
            .then((res) => {
              response = res;
              this.Ratings = res?.data?.data || [];
              this.RatingsData = res?.data?.data || [];
              this.normalizeRatings();
              return this.$_getRanking();
            })
            .then(() => this.getPerfomanceRating())
            .then(() => {
              this.$toasted.success(response.data.message, { duration: 5000 });
              this.closeSideBar();
              this.loading = false;
            })
            .catch(() => {
              this.Ratings = [];
              this.$toasted.error("Error in creating Performance rating", {
                duration: 5000,
              });
              this.loading = false;
            });
        }
        else if (this.mode === 'floating') {
          this.$_getPerfomanceRatingByType('floating')
            .then(() => {
              this.Ratings = [];
              this.RatingsData = [];
              this.normalizeRatings();
              return this.$_getRanking();
            })
            .then(() => this.getPerfomanceRating())
            .then(() => {
              this.$toasted.success("Performance rating updated successfully", { duration: 5000 });
              this.closeSideBar();
              this.loading = false;
            })
            .catch(() => {
              this.Ratings = [];
              this.$toasted.error("Error in creating Performance rating", {
                duration: 5000,
              });
              this.loading = false;
            });
        }
      },

    closeSideBar() {
      this.showModal = false;
    },

    descModal() {
      this.$emit("openDescription");
    },

    normalizeRatings() {
      this.descriptions = [];
      const sortedRatings = this.Ratings.sort((a, b) => a.rating - b.rating);

      this.Ratings = sortedRatings;

      this.lowest_value = Math.min.apply(
        this.Ratings,
        this.Ratings.map((item) => item.rating)
      );
      this.highest_value = Math.max.apply(
        this.Ratings,
        this.Ratings.map((item) => item.rating)
      );
      this.lowestValue = this.lowest_value;
      this.highestValue = this.highest_value;

      this.Ratings.map((ratings) => {
        this.descriptions.push(ratings.description);
        return {};
      });
    },

    normalizeRankings(rankings) {
      this.Ranking = [];
      rankings.filter((ranking) => {
        this.Ranking.push({
          id: ranking.id,
          from: ranking.from,
          to: ranking.to,
          description: ranking.description,
          flag: ranking.flag.name,
        });
        return {};
      });
    },

    async getPerfomanceRating() {
      try {
        const getPerfomanceRating = await this.$_getPerfomanceRating();
        const getRanking = await this.$_getRanking();

        let rankings = [];

        if (getRanking.data.rankings.length === 0) {
          this.Ranking = [];
        } else {
          rankings = getRanking.data.rankings;
          this.Ratings = getPerfomanceRating.data.PerformanceRatings;
          this.RatingsData = getPerfomanceRating.data.PerformanceRatings;
          this.isSettingModeFromData = true;
          this.mode = this.Ratings[0].type;
          if (this.Ratings.length === 0) {
            const ratingsArr = [];
            this.ratingPayload = ratingsArr;
          } else {
            this.normalizeRatings();
          }
        }

        this.normalizeRankings(rankings);

        this.loading = false;
      } catch (err) {
        throw new Error(err);
      }
    },

    handleAppraisalMethodChange(value) {
      this.selectedAppraisalMethodName = this.appraisal_options.find(option => option.value === value)?.name;
      this.showAppraisalConfirmationModal = true;
    },

    async confirmAppraisalMethodChange() {
      this.showAppraisalConfirmationModal = false;
      try {
        await this.$handlePrivilege(
          "performance",
          "editPerformanceAppraisalMethod"
        );

        const payload = {
          method: this.settingsPayload.appraisalMethod
        };

        await this.$_switchAppraisal(payload, this.settingsPayload.settingId);
        this.$toasted.success("Appraisal method updated successfully.", { duration: 5000 });
        await this.getPerfomanceRating();
      } catch (error) {
        this.$toasted.error(
          error.response?.data?.message || "Failed to update appraisal method",
          {
            duration: 5000,
          }
        );
      }
    },

    async getAppraisalMethodSettings() {
      try {
        const response = await this.$_getPerformanceGeneralSetting();
        if (response?.data?.PerformanceSettings[0]) {
          this.settingsPayload.appraisalMethod = response.data.PerformanceSettings[0].midTermAppraisalMethod;
          this.settingsPayload.settingId = response.data.PerformanceSettings[0].id;
        }
      } catch (error) {
        throw new Error(error);
      }
    },

    agreementEdit(value) {
      this.settingsPayload.editDocument = value;
    }
  },

  async mounted() {
    try {
      await this.$handlePrivilege(
        "performance",
        "accessPerformanceRatingsRankingsSettings"
      );
      this.getPerfomanceRating();
      await this.getAppraisalMethodSettings();
    } catch (error) {
      this.errorPage = true;
    }
  },

};
</script>

<style>
.label label {
  display: block !important;
}
</style>

<style scoped>
.dashed_border {
  position: static;
  width: 97.5%;
  height: 0px;
  left: 18px;
  top: 210px;
  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 0;
  margin: 18px 0px;
}
::v-deep label.col-span-4 {
height: 30px !important;
}
.last_description_text {
  font-weight: 600;
  font-size: 14px;
  line-height: 120%;
  letter-spacing: 0.015em;
  text-transform: uppercase;
  color: #f15a29;
  margin: 5.5px 0px;
  cursor: pointer;
}
.description_text {
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 120%;
  letter-spacing: 0.015em;
  color: #333333;
  margin: 5.5px 0px;
}
.button {
  width: 7.875rem;
  height: 2.75rem;
  padding: 12px, 15px, 12px, 15px;
}
.card {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px;
  position: static;
  width: 100%;
  height: 136px;
  left: 0px;
  top: 38px;
  background: #f7f7ff;
  border-radius: 5px;
  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 0;
  margin: 12px 0px;
}
.half {
  flex-basis: 50%;
}
.border-r-2 {
  border-right: 1px solid #878e99;
}
.text-rating {
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 130%;
  letter-spacing: 0.5px;
  color: #333333;
}
.lowest {
  font-style: normal;
  font-weight: 800;
  font-size: 42px;
  line-height: 120%;
  letter-spacing: 0.015em;
  color: #ea3c53;
}
.highest {
  font-style: normal;
  font-weight: 800;
  font-size: 42px;
  line-height: 120%;
  letter-spacing: 0.015em;
  color: #13b56a;
}
/* .rantings_height{
  height: 38px;
} */
.rankingtable {
  height: 397px;
  margin: 22px 0px 0px 0px;
}
.editRating__block {
  width: 480px;
  margin: 18px 0px;
}
.ratingInput {
  width: 231px;
  margin: 0px 18px;
}
.ratingInput__label {
  height: 18px;
  font-size: 14px;
  color: #333333;
  line-height: 130%;
  letter-spacing: 0.5px;
  margin: 12px 0px;
}
.editRating__subheader {
  height: 19px;
  margin: 18px 0px;
}
.ratingstable {
  width: 480px;
  margin: 18px 0px;
}
.ratingstable__header {
  height: 33px;
  border-radius: 5px;
  padding: 8px;
  margin: 0px 0px 4px 0px;
  background-color: #f7f7ff;
  color: #878e99;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.035em;
  text-transform: uppercase;
}
.column__xs {
  width: 48px;
  background-color: #eeeeee;
}
.column__lg {
  width: 395px;
}
.warning {
  display: flex;
  align-items: start;
  letter-spacing: 0.5px;
  background-color: #E9932314;
  padding: 10px;
  border-radius: 8px;
  font-size: 12px;
  color: #333333;
  border: 1px solid #E993235C;
}
</style>
